import { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { T, Var, Num, useGT } from 'gt-react';
import { MAX_DATA_SOURCE_LOAD_CONCURRENCY } from '@erp/shared-types';
import type { DataSource, PreviewDataSourceLoadResponse } from '@erp/rpc';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/context/ToastContext';
import {
  START_DATA_SOURCE_LOAD_MUTATION_KEY,
  useDataSourceLoadPreview,
  useDataSources,
  useStartDataSourceLoad,
} from '@/api/rpc/hooks/core/useDataSources';
import {
  LoadSettingsFields,
  RetransformUnchangedCheckbox,
  SqlModeCheckbox,
} from './DataSourceLinkLoads';
import {
  EMPTY_LOAD_SETTINGS,
  parseLoadSettings,
  parsePositive,
} from './loadSettings';

interface DataSourceLoadAllDialogProps {
  source: DataSource;
  open: boolean;
  onClose: () => void;
}

/**
 * "Load all": one direct load per loadable link of a data source, in the
 * order the server plans (item master first, then the other product schemas,
 * then the rest), one link at a time unless told otherwise. Any link can be
 * unticked to leave it out of the batch.
 */
export function DataSourceLoadAllDialog({
  source,
  open,
  onClose,
}: DataSourceLoadAllDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title: start a batch direct load of every link of a data source">
              Load all links of <Var>{source.title}</Var>
            </T>
          </DialogTitle>
        </DialogHeader>
        {open && <DataSourceLoadAllForm source={source} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

interface StartDataSourceLoadDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The same batch load, started from the loads page: pick the data source
 * first, then the links. Each data source is listed under its group.
 */
export function StartDataSourceLoadDialog({
  open,
  onClose,
}: StartDataSourceLoadDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title: start a batch direct load from the loads page">
              Start a load
            </T>
          </DialogTitle>
        </DialogHeader>
        {open && <StartDataSourceLoadForm onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function StartDataSourceLoadForm({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = useDataSources();
  const [sourceId, setSourceId] = useState<string | null>(null);
  // A start in flight belongs to the form below; switching sources would
  // unmount it while its request still closes this dialog when it lands.
  const starting =
    useIsMutating({ mutationKey: START_DATA_SOURCE_LOAD_MUTATION_KEY }) > 0;

  const sources =
    data?.groups.flatMap((group) =>
      group.dataSources.flatMap((source) =>
        source.isSource ? [{ source, groupTitle: group.title }] : [],
      ),
    ) ?? [];
  const selected = sources.find((s) => s.source.id === sourceId)?.source;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="direct-load-data-source">
          <T context="batch direct load: data source picker label">
            Data source
          </T>
        </Label>
        <Select
          value={sourceId ?? ''}
          onValueChange={setSourceId}
          disabled={isLoading || starting || sources.length === 0}
        >
          <SelectTrigger id="direct-load-data-source">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {sources.map(({ source, groupTitle }) => (
              <SelectItem key={source.id} value={source.id}>
                {groupTitle} / {source.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error && (
        <AlertContainer variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </AlertContainer>
      )}
      {selected ? (
        <DataSourceLoadAllForm
          key={selected.id}
          source={selected}
          onClose={onClose}
        />
      ) : (
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <T context="cancel batch direct load button">Cancel</T>
          </Button>
        </DialogFooter>
      )}
    </div>
  );
}

function DataSourceLoadAllForm({
  source,
  onClose,
}: {
  source: DataSource;
  onClose: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const {
    data: preview,
    isLoading,
    error,
  } = useDataSourceLoadPreview(source.id, true);
  const start = useStartDataSourceLoad();
  const [concurrency, setConcurrency] = useState('1');
  const [settings, setSettings] = useState(EMPTY_LOAD_SETTINGS);
  // The links in the batch, by id; null until the user touches the picker,
  // meaning every link shown. Once touched it is an explicit set taken from
  // the links on screen at that moment, so a preview refetch that adds a
  // link shows it unticked rather than quietly adding it to the batch.
  const [picked, setPicked] = useState<ReadonlySet<string> | null>(null);

  const concurrencyValue = parsePositive(concurrency, {
    max: MAX_DATA_SOURCE_LOAD_CONCURRENCY,
  });
  const parsed = parseLoadSettings(settings);
  const blocked = (preview?.runningLoadIds.length ?? 0) > 0;
  const nothingToLoad = preview !== undefined && preview.links.length === 0;
  const invalid = parsed.invalid || concurrencyValue === null;
  const shownLinkIds = preview?.links.map((link) => link.linkId) ?? [];
  const pickedLinkIds =
    picked === null
      ? shownLinkIds
      : shownLinkIds.filter((linkId) => picked.has(linkId));

  function pickOne(linkId: string, on: boolean) {
    setPicked((prev) => {
      const next = new Set(prev ?? shownLinkIds);
      if (on) next.add(linkId);
      else next.delete(linkId);
      return next;
    });
  }

  function pickAll(on: boolean) {
    setPicked(on ? new Set(shownLinkIds) : new Set());
  }

  async function submit() {
    if (
      parsed.invalid ||
      concurrencyValue === null ||
      !preview ||
      pickedLinkIds.length === 0
    ) {
      return;
    }
    try {
      const { links } = await start.mutateAsync({
        dataSourceId: source.id,
        linkIds: pickedLinkIds,
        concurrency: concurrencyValue,
        chunkBytes: parsed.chunkBytes,
        parallelism: parsed.parallelism,
        skipUnchangedRows: parsed.skipUnchangedRows,
        sqlMode: parsed.sqlMode,
      });
      showToast({
        color: 'success',
        message: gt('Batch started: {count} loads', {
          count: links.length,
          context: 'toast after starting a batch direct load',
        }),
      });
      onClose();
    } catch (err) {
      showToast({
        color: 'error',
        message:
          err instanceof Error
            ? err.message
            : gt('Failed to start batch load', { context: 'toast error' }),
      });
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        <T context="batch direct load dialog description">
          Runs a direct load of each ticked link below, in this order, each as
          its own load with its own history. Links run one at a time unless you
          raise the concurrency: the product-feeding links all upsert the same
          product rows, so run in parallel they mostly wait on each other.
        </T>
      </p>

      {isLoading && (
        <div className="space-y-2">
          <div className="h-8 rounded-md bg-muted animate-pulse" />
          <div className="h-8 rounded-md bg-muted animate-pulse" />
        </div>
      )}
      {error && (
        <AlertContainer variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </AlertContainer>
      )}
      {preview && blocked && (
        <AlertContainer variant="destructive">
          <AlertDescription>
            <T context="batch direct load blocked by a running load">
              A direct load is already running:{' '}
              <Var>{preview.runningLoadIds.join(', ')}</Var>. Wait for it to
              finish before loading a data source.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}
      {preview && nothingToLoad && (
        <AlertContainer>
          <AlertDescription>
            <T context="batch direct load: no loadable link">
              None of this data source's links can be loaded.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}

      {preview && (
        <LinkPicker
          preview={preview}
          pickedLinkIds={pickedLinkIds}
          onPick={pickOne}
          onPickAll={pickAll}
        />
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label htmlFor="direct-load-concurrency">
            <T context="batch direct load: links loaded at once field label">
              Links at once
            </T>
          </Label>
          <Input
            id="direct-load-concurrency"
            type="number"
            min={1}
            max={MAX_DATA_SOURCE_LOAD_CONCURRENCY}
            value={concurrency}
            aria-invalid={concurrencyValue === null}
            onChange={(e) => setConcurrency(e.target.value)}
          />
        </div>
        <LoadSettingsFields
          value={settings}
          onChange={setSettings}
          defaults={preview?.defaults}
        />
      </div>
      <RetransformUnchangedCheckbox
        checked={settings.retransformUnchanged}
        onCheckedChange={(retransformUnchanged) =>
          setSettings({ ...settings, retransformUnchanged })
        }
      />
      <SqlModeCheckbox
        checked={settings.sqlMode}
        onCheckedChange={(sqlMode) => setSettings({ ...settings, sqlMode })}
      />

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          <T context="cancel batch direct load button">Cancel</T>
        </Button>
        <Button
          onClick={submit}
          disabled={
            start.isPending ||
            invalid ||
            !preview ||
            blocked ||
            nothingToLoad ||
            pickedLinkIds.length === 0
          }
        >
          <T context="start batch direct load button">
            Load <Num>{pickedLinkIds.length}</Num> links
          </T>
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * The loadable links in load order, each with a tick to keep it in the
 * batch, then the skipped ones with their reason. Shared with the Start Data
 * Source Load step's config, which shows it read-only when the workflow is.
 */
export function LinkPicker({
  preview,
  pickedLinkIds,
  onPick,
  onPickAll,
  disabled = false,
}: {
  preview: Pick<PreviewDataSourceLoadResponse, 'links' | 'skipped'>;
  pickedLinkIds: readonly string[];
  onPick: (linkId: string, picked: boolean) => void;
  onPickAll: (picked: boolean) => void;
  disabled?: boolean;
}) {
  const pickedSet = new Set(pickedLinkIds);
  const pickedCount = pickedLinkIds.length;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>
          <T context="batch direct load: how many links are ticked">
            <Num>{pickedCount}</Num> of <Num>{preview.links.length}</Num> links
            picked
          </T>
        </span>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          disabled={disabled || pickedCount === preview.links.length}
          onClick={() => onPickAll(true)}
        >
          <T context="batch direct load: tick every link">All</T>
        </Button>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          disabled={disabled || pickedCount === 0}
          onClick={() => onPickAll(false)}
        >
          <T context="batch direct load: untick every link">None</T>
        </Button>
      </div>
      <ol className="max-h-64 overflow-y-auto rounded-md border divide-y text-sm">
        {preview.links.map((link, index) => {
          const picked = pickedSet.has(link.linkId);
          return (
            <li key={link.linkId}>
              <Label
                className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 font-normal ${picked ? '' : 'text-muted-foreground'}`}
              >
                <Checkbox
                  checked={picked}
                  disabled={disabled}
                  onCheckedChange={(v) => onPick(link.linkId, v === true)}
                />
                <span className="w-5 shrink-0 text-right tabular-nums text-muted-foreground">
                  {index + 1}.
                </span>
                <span className="truncate font-medium">{link.label}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {link.stepType}
                </span>
              </Label>
            </li>
          );
        })}
        {preview.skipped.map((link) => (
          <li
            key={link.linkId}
            className="flex items-center gap-2 px-3 py-1.5 text-muted-foreground"
          >
            <span className="w-4 shrink-0" aria-hidden />
            <span className="w-5 shrink-0" aria-hidden />
            <span className="truncate line-through">{link.label}</span>
            <span className="ml-auto shrink-0 text-xs" title={link.reason}>
              <T context="batch direct load: link skipped, with the reason">
                Skipped: <Var>{link.reason}</Var>
              </T>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
